<template>
	<button
		:class="[
			$style['app-button'],
			{
				[$style['app-button--white']]: white,
				[$style['app-button--danger']]: danger,
				[$style['app-button--warning']]: warning,
				[$style['app-button--success']]: success,
				[$style['app-button--icon']]: icon,
				[$style['app-button--full']]: full,
				[$style['app-button--loading']]: loading
			}
		]"
		@click="$emit('click', $event)"
	>
		<BaseRipple />
		<span :class="$style['app-button__content']">
			<slot />
		</span>
	</button>
</template>

<script>
export default {
	name: 'BaseButton',
	emits: ['click'],
	props: {
		icon: { type: [Boolean, String], default: false },
		white: { type: [Boolean, String], default: false },
		warning: { type: [Boolean, String], default: false },
		success: { type: [Boolean, String], default: false },
		danger: { type: [Boolean, String], default: false },
		full: { type: [Boolean, String], default: false },
		loading: { type: [Boolean, String], default: false }
	}
}
</script>

<style lang="scss" module>
.app-button {
    position: relative;
    overflow: hidden;
    display: flex;
    justify-content: center;
    align-items: center;
    appearance: none;
    border: 0;
    background-color: getColor(primary-button);
    color: getColor(white);
    text-transform: uppercase;
    font-weight: bold;
    font-size: 11px;
    padding: 8px 30px;
    border-radius: 20px;
    height: 40px;
    box-shadow: 0 2px 6px 0 getColor(shadow-light);
    transition:
        background-color 0.15s ease,
        box-shadow 0.15s ease,
        color 0.15s ease;
    outline: 0;
    transform: translateZ(0);

    &__content {
        position: relative;
        z-index: 2;
        display: flex;
        align-items: center;
        width: 100%;
        justify-content: center;
    }

    &--full {
        width: 100%;
    }

    &--icon {
        svg {
            margin-right: 10px;
        }
    }

    &--loading {
        color: transparent !important;
        pointer-events: none;
        transition: none !important;

        &::before {
            content: "";
            position: absolute;
            z-index: 3;
            animation: spin 0.5s infinite linear;
            border: 2px solid getColor(white);
            border-radius: 100%;
            border-right-color: transparent;
            border-top-color: transparent;
            display: block;
            height: 24px;
            width: 24px;
        }
    }

    &:disabled {
        cursor: not-allowed !important;
        color: getColor(disabled-link) !important;
        background-color: getColor(button-disabled) !important;
        box-shadow: none !important;
    }

    &:not(:disabled) {
        &:hover {
            cursor: pointer;
            background-color: getColor(primary-button-hover);
            box-shadow: 0 3px 8px 0 getColor(shadow-light);
        }

        &:active {
            background-color: getColor(primary-active);
            box-shadow: 0 1px 3px 0 getColor(shadow-light);
        }

        &:focus-visible {
            outline: 2px solid getColor(stroke);
            outline-offset: 2px;
        }
    }

    &--white {
        background-color: getColor(button-white);
        color: getColor(text);
        box-shadow: 0 2px 6px 0 getColor(shadow-light);

        &:hover:not(:disabled) {
            background-color: getColor(surface-elevated-high);
        }

        &:active:not(:disabled) {
            background-color: getColor(surface-active);
        }

        &.app-button--loading {
            &::before {
                border-color: getColor(primary);
                border-right-color: transparent;
                border-top-color: transparent;
            }
        }
    }

    &--danger {
        background-color: getColor(danger);
        color: getColor(white);

        &:hover:not(:disabled) {
            background-color: getColor(danger-hover);
        }

        &:active:not(:disabled) {
            background-color: getColor(danger-active);
        }
    }

    &--warning {
        background-color: getColor(warning);
        color: getColor(dark-text);

        &:hover:not(:disabled) {
            background-color: getColor(warning-hover);
        }

        &:active:not(:disabled) {
            background-color: getColor(warning-active);
        }

        &.app-button--loading {
            &::before {
                border-color: getColor(dark-text);
                border-right-color: transparent;
                border-top-color: transparent;
            }
        }
    }

    &--success {
        background-color: getColor(success);
        color: getColor(white);

        &:hover:not(:disabled) {
            background-color: getColor(success-hover);
        }

        &:active:not(:disabled) {
            background-color: getColor(success-active);
        }
    }

    // ------------------------------------------------------------
    // DARK MODE ONLY: subtle borders for shape definition
    // ------------------------------------------------------------
    @include dark-mode {
        border: 1px solid transparent;
        box-shadow: 0 0 0 1px getColor(border-light);

        &--white {
            border: 1px solid transparent;
            box-shadow: 0 0 0 1px getColor(border-minor);
        }

        &--danger {
            border: 1px solid transparent;
            box-shadow: 0 0 0 1px transparentize(getColor(danger), 0.7);
        }

        &--warning {
            border: 1px solid transparent;
            box-shadow: 0 0 0 1px transparentize(getColor(warning), 0.7);
        }

        &--success {
            border: 1px solid transparent;
            box-shadow: 0 0 0 1px transparentize(getColor(success), 0.7);
        }

        &:disabled {
            border: 1px solid transparent;
            box-shadow: 0 0 0 1px getColor(disabled);
        }
    }
}

@keyframes spin {
    from {
        transform: rotate(0);
    }

    to {
        transform: rotate(359deg);
    }
}
</style>
